/**
 * Backend Factory
 *
 * Creates and manages the Appwrite backend provider.
 *
 * Usage:
 *   import { backendProvider } from './services/backend/BackendFactory';
 *   const user = await backendProvider.auth.signInWithGoogle();
 */

import { IBackendProvider } from './interfaces/IBackendProvider';
import { AppwriteProvider } from './appwrite/AppwriteProvider';
import { StorageBucketIds } from './appwrite/AppwriteStorageService';

/**
 * Create Appwrite provider instance
 */
function createAppwriteProvider(): IBackendProvider {
  console.log('📱 Creating Appwrite backend provider...');

  // Validate required environment variables
  const endpoint = import.meta.env.VITE_APPWRITE_ENDPOINT;
  const projectId = import.meta.env.VITE_APPWRITE_PROJECT_ID;
  const brainDatabaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID;
  const defaultDatabaseId = import.meta.env.VITE_APPWRITE_USERS_DATABASE_ID;

  // Storage bucket IDs
  const profilePicturesBucketId = import.meta.env.VITE_APPWRITE_BUCKET_PROFILE_PICTURES;
  const logosBucketId = import.meta.env.VITE_APPWRITE_BUCKET_LOGOS;
  const workforceBucketId = import.meta.env.VITE_APPWRITE_BUCKET_WORKFORCE;
  const csvUploadsBucketId = import.meta.env.VITE_APPWRITE_BUCKET_CSV_UPLOADS;
  const leadReportsBucketId = import.meta.env.VITE_APPWRITE_BUCKET_LEAD_REPORTS || 'lead-reports';
  const clientLogosBucketId = import.meta.env.VITE_APPWRITE_BUCKET_CLIENT_LOGOS || 'client-logos';

  // Collection IDs for brain database (campaigns/leads)
  const campaignsCollectionId = import.meta.env.VITE_APPWRITE_CAMPAIGNS_COLLECTION_ID;
  const leadsCollectionId = import.meta.env.VITE_APPWRITE_LEADS_COLLECTION_ID;

  // Collection IDs for default database (users)
  const usersCollectionId = import.meta.env.VITE_APPWRITE_USERS_COLLECTION_ID;

  if (!endpoint || !projectId) {
    throw new Error(
      'Appwrite configuration missing. Please set VITE_APPWRITE_ENDPOINT and VITE_APPWRITE_PROJECT_ID in .env.local'
    );
  }

  if (!brainDatabaseId || !defaultDatabaseId) {
    throw new Error(
      'Appwrite database IDs missing. Please set VITE_APPWRITE_DATABASE_ID and VITE_APPWRITE_USERS_DATABASE_ID in .env.local'
    );
  }

  if (!profilePicturesBucketId || !logosBucketId || !workforceBucketId) {
    throw new Error(
      'Appwrite storage bucket IDs missing. Please set VITE_APPWRITE_BUCKET_PROFILE_PICTURES, VITE_APPWRITE_BUCKET_LOGOS, and VITE_APPWRITE_BUCKET_WORKFORCE in .env.local'
    );
  }

  if (!campaignsCollectionId || !leadsCollectionId) {
    throw new Error(
      'Appwrite collection IDs missing. Please set VITE_APPWRITE_CAMPAIGNS_COLLECTION_ID and VITE_APPWRITE_LEADS_COLLECTION_ID in .env.local'
    );
  }

  // Create storage buckets configuration
  const storageBuckets: StorageBucketIds = {
    profilePictures: profilePicturesBucketId,
    logos: logosBucketId,
    workforceSignatures: workforceBucketId,
    csvUploads: csvUploadsBucketId || 'csv-upload-folder',
    leadReports: leadReportsBucketId,
    clientLogos: clientLogosBucketId,
  };

  // Create collection ID mappings for name -> ID resolution
  const brainCollectionIds: Record<string, string> = {
    campaigns: campaignsCollectionId,
    leads: leadsCollectionId,
  };

  const defaultCollectionIds: Record<string, string> = {
    users: usersCollectionId || 'users', // Use 'users' as fallback
  };

  const provider = new AppwriteProvider(
    endpoint,
    projectId,
    brainDatabaseId,
    defaultDatabaseId,
    storageBuckets,
    brainCollectionIds,
    defaultCollectionIds
  );

  console.log('✅ Appwrite provider created with collection ID mappings');
  return provider;
}

/**
 * Singleton backend provider instance
 * This is initialized when the module is imported
 */
export const backendProvider: IBackendProvider = createAppwriteProvider();

/**
 * Initialize the backend provider
 * Call this at app startup (in main.tsx or App.tsx)
 */
export async function initializeBackend(): Promise<void> {
  try {
    console.log('🚀 Initializing backend provider...');
    await backendProvider.initialize();
    console.log(`✅ Backend initialized: ${backendProvider.getName()}`);
  } catch (error: any) {
    console.error('❌ Failed to initialize backend:', error);
    throw error;
  }
}
